import React, { useState, useEffect } from 'react';
import {
  Accessibility, BedDouble, Banknote, Utensils, FileText, Coffee, HeartPulse,
  Mic, Building2, Sparkles, Star, Wifi, IdCard, Car, Camera, Ticket, Users,
  Hash, Phone, Globe, Mail, ChevronRight
} from 'lucide-react';
import { useAccessibility } from '../contexts/AccessibilityContext';
import './About.css';
import { useFocusableNavigation } from '../hooks/useFocusableNavigation';

// Mirrors the iOS app's Conference A-Z (UsefulInfoView.swift) so both apps
// show the same conference logistics information.
const CONFERENCE_AZ = [
  {
    title: 'Accessible Entrances',
    icon: Accessibility,
    content: 'Please see the yellow dots on the campus map for accessible entrances to each building.',
  },
  {
    title: 'Accommodation',
    icon: BedDouble,
    content: (
      <>
        <p>Most rooms are in Storm Jameson Court.</p>
        <p><strong>Departure</strong> — On your last day, you must hand your room keys in at Storm Jameson Court reception by 10.00. AAC Users and their PAs will have an additional one hour (until 11.00) to depart. You will be charged £20 by the University if your key card is not returned.</p>
        <p><strong>Luggage</strong> — There will be a luggage store available on Tuesday at Storm Jameson Court. Ask at reception when you hand in your key card.</p>
        <p><strong>Facilities</strong> — Kitchen and lounge areas are shared between 6–8 rooms. Tea and coffee making facilities are provided in the shared kitchens or in the bedrooms. An iron and ironing board are available in each kitchen. If there are any issues with your room, please go to Storm Jameson Court reception. The university's sports facilities, including a pool at The Edge, are available free of charge to residential delegates.</p>
      </>
    ),
  },
  {
    title: 'Banks, Shops & Facilities',
    icon: Banknote,
    content: 'There are cash points located within the Student Union building adjacent to the Refectory. The nearest Post Office is in the St John’s Centre in the city centre. City centre shops generally open 9.00–17.00 Monday to Saturday, and 11.00–17.00 on Sundays. The Student Union building on campus houses a number of shops, bars, cafes and a co-op.',
  },
  {
    title: 'Catering & Special Diets',
    icon: Utensils,
    content: (
      <>
        <p>Breakfast is served in the Refectory from 7.30–9.00.</p>
        <p>Tea/coffee and lunch are served in the Parkinson Building exhibition area.</p>
        <p>Evening meals take place in the Refectory.</p>
        <p>Special diets are catered for where requested — at lunchtime your individual plated lunch will be found at the counter opposite the coffee bar in Parkinson building. If you have any problems with the food provided for your special diet, please introduce yourself to the Refectory Duty Manager.</p>
        <p><strong>We appreciate AAC users may need longer to eat and welcome AAC users / their PAs to go to the front of any meal queues</strong> — please respect this.</p>
      </>
    ),
  },
  {
    title: 'Certificate of Attendance',
    icon: FileText,
    content: 'If you require a certificate of attendance, please email admin@communicationmatters.org.uk',
  },
  {
    title: 'Chill Out Zones',
    icon: Coffee,
    content: 'There is a quiet area for use throughout the day and evening at Storm Jameson Court foyer. An additional quiet area is made available during lunchtimes in the Baines Wing coffee bar and Parkinson room B08.',
  },
  {
    title: 'Emergency & Medical Help',
    icon: HeartPulse,
    content: (
      <>
        <p>For emergencies call University Security (24 hours) on 0113 343 2222.</p>
        <p><strong>Mobile Helpline</strong> — If you can't find anyone on-hand to help you, please ring the CM Conference Line: 07908 929868.</p>
        <p><strong>First Aid on Campus</strong> — Please contact a member of staff in the building, or in the event of a serious problem, contact University of Leeds Security on 0113 343 5495 (24 hours).</p>
        <p><strong>Hospital</strong> — The nearest emergency department is at the Leeds General Infirmary, telephone 0113 243 2799.</p>
      </>
    ),
  },
  {
    title: 'Entertainment & Social',
    icon: Mic,
    content: 'Evening entertainment and social events run throughout the conference, including a drinks reception and the 40th Anniversary Celebratory Dinner on Monday evening — see the Timetable for full details.',
  },
  {
    title: 'Exhibition',
    icon: Building2,
    content: 'The exhibition is located in the exhibition hall. It opens with a Welcome Drinks Reception for all delegates on Sunday evening, and continues through Monday and Tuesday — see the Timetable for exact times.',
  },
  {
    title: 'Fancy Dress',
    icon: Sparkles,
    content: 'Fancy dress can be worn to the drinks reception and the 40th Anniversary Celebratory Dinner on Monday evening (1986-themed, optional). The Chair and Trustees will award prizes for the best group and best individual costumes.',
  },
  {
    title: 'Feedback',
    icon: Star,
    content: 'You will find a paper copy of the conference feedback form in your welcome pack. Please complete this before you leave the conference. Alternatively, you may prefer to complete our online feedback form at www.communicationmatters.org.uk.',
  },
  {
    title: 'Internet Access (Wi-Fi)',
    icon: Wifi,
    content: 'Wi-Fi internet access is available in seminar rooms, the exhibition area and accommodation. If you experience any problems with the Wi-Fi please speak to a conference organiser.',
  },
  {
    title: 'Lanyards',
    icon: IdCard,
    content: (
      <>
        <p>Please wear your badge during the conference.</p>
        <p>• Blue Lanyards: Attending conference for the first time — please make them welcome!</p>
        <p>• Red Lanyards: Trustees and CM Staff.</p>
        <p>• Green Lanyards: Exhibition-only passes.</p>
        <p>• Yellow Lanyards: Conference Makers.</p>
      </>
    ),
  },
  {
    title: 'Parking',
    icon: Car,
    content: (
      <>
        <p>Blue Badge parking has been pre-arranged for all who require it — staff will be available on arrival to direct you to available spaces.</p>
        <p>For all other on-campus parking, an ANPR system is in operation across campus and automatically issues fines, so make sure to enter your parking code at a ticket machine on arrival.</p>
        <p>Electric vehicles: charging points are available across campus, free of charge for visitors (usual parking costs still apply).</p>
      </>
    ),
  },
  {
    title: 'Photography',
    icon: Camera,
    content: 'An official photographer will be taking photographs during the conference.',
  },
  {
    title: 'Posters',
    icon: FileText,
    content: 'The poster exhibition takes place on Monday and Tuesday. Authors will be available to discuss their poster during morning breaks and lunchtimes — see the Posters tab for the full list.',
  },
  {
    title: 'Raffle',
    icon: Ticket,
    content: 'Raffle tickets will be on sale at the Drinks Reception and during the Conference Dinner on Monday evening. Money raised from the raffle helps provide subsidised conference places for AAC users.',
  },
  {
    title: 'Room Capacity',
    icon: Users,
    content: 'For health and safety reasons, we may need to restrict the number of delegates allowed per session. The session rooms are mainly left clear of furniture to make room for wheelchair users, but please help yourself to a chair and desk from the side of the room if needed.',
  },
  {
    title: 'Social Media',
    icon: Hash,
    content: 'Feel free to post about conference content on social media, but taking photos is not permitted during sessions.',
  },
  {
    title: 'Toilets & Changing Places',
    icon: Accessibility,
    content: 'Toilets are located in every building across campus, with accessible toilets found on the ground floor. Accessible toilets with changing facilities (changing bed, hoist) are available in several buildings — if you need help finding any toilet facilities, please ask one of the Conference Makers (yellow lanyards).',
  },
];

const EMERGENCY_CONTACTS = [
  { name: 'Emergency Services', number: '999', isPrimary: true },
  { name: 'University Security', number: '0113 343 2222', isPrimary: false },
  { name: 'CM Conference Line', number: '07908 929868', isPrimary: false },
  { name: 'First Aid', number: '0113 343 5495', isPrimary: false },
  { name: 'Leeds General Infirmary', number: '0113 243 2799', isPrimary: false },
];

const TEAM_MEMBERS = [
  { name: 'Helen Copeland', role: 'Operations Manager', bio: 'Handles the day-to-day running of Communication Matters, including membership renewals, conference registration, event management, communications, and book-keeping.', section: 'Staff' },
  { name: 'Hayley Leith', role: 'Administrator', bio: 'Handles the day-to-day running of Communication Matters, including membership renewals, conference registration, event management, communications, and book-keeping.', section: 'Staff' },
  { name: 'Andrea Sharples', role: 'Chair', bio: 'Clinical Director at ATtherapy, an independent speech and language therapy service. Continues developing research skills in the AAC field.', section: 'Trustees' },
  { name: 'Saffron Murphy-Mann', role: 'Deputy Chair', bio: 'Involved in AAC since 2015 as a specialist AAC teacher. Currently works as an AAC Consultant nationwide.', section: 'Trustees' },
  { name: 'Tina Voizey', role: 'Treasurer', bio: 'Special educator with over 30 years’ experience working with learners with complex disabilities, many of whom use AAC.', section: 'Trustees' },
  { name: 'Debbie Breslin', role: 'Trustee', bio: 'Over 22 years working in the disability community, particularly 17 years in the AAC/AT sector.', section: 'Trustees' },
  { name: 'Katherine Broomfield', role: 'Trustee', bio: 'Speech and language therapist and researcher with significant experience working with people who have complex communication needs.', section: 'Trustees' },
  { name: 'Claire Harrison', role: 'Trustee', bio: 'Teacher for nearly thirty years supporting children and young people with additional support needs.', section: 'Trustees' },
  { name: 'Helen Hewson', role: 'Trustee', bio: 'AAC user since age 4, passionate about supporting younger AAC users to achieve their communication potential. Runs the AAC Focus Group at Communication Matters.', section: 'Trustees' },
  { name: 'Sarah McPoland', role: 'Trustee', bio: 'Advanced Specialist Speech and Language Therapist with over eleven years’ paediatric experience.', section: 'Trustees' },
  { name: 'Jessica MacRae-Pemberton', role: 'Trustee', bio: 'Over 12 years working in AAC and AT roles, now UK & International Sales Manager for Smartbox Assistive Technology.', section: 'Trustees' },
  { name: 'Beth Moulam', role: 'Trustee', bio: 'Lifelong AAC user and advocate with lived experience. Paralympian representing GB in Boccia at Tokyo 2020.', section: 'Trustees' },
  { name: 'Charlie Moran', role: 'Trustee', bio: 'Specialist Speech and Language Therapist with Barnsley Assistive Technology Team.', section: 'Trustees' },
  { name: 'Michelle Paton', role: 'Trustee', bio: 'Speech and Language Therapist in NHS supporting children with additional support needs in Greater Glasgow and Clyde.', section: 'Trustees' },
  { name: 'Bob Sagoo', role: 'Trustee', bio: 'Over 10 years in the AAC/AT industry. Father of Harchie, whose speech and motor skills were impacted by viral encephalitis at five months old.', section: 'Trustees' },
  { name: 'Theresa Tanchak', role: 'Trustee', bio: 'Speech and Language Therapist and Assistive Technology specialist with over 40 years’ AAC experience.', section: 'Trustees' },
  { name: 'Christopher Bugaj', role: 'Patron', bio: 'Founding member of the Assistive Technology Team for Loudoun County Public Schools (Virginia, USA). Co-hosts the Talking With Tech podcast.', section: 'Patrons' },
  { name: 'Lee Ridley', role: 'Patron', bio: 'Uses a communication aid and works to normalise alternative communication through comedy, media appearances, and public engagement.', section: 'Patrons' },
];

const initials = (name) => name.split(' ').map((p) => p[0]).slice(0, 2).join('');

const InfoDisclosure = ({ title, icon: Icon, content, isOpen, onToggle }) => (
  <div className="az-item">
    <button
      type="button"
      className="az-item-header"
      aria-expanded={isOpen}
      onClick={onToggle}
    >
      <Icon size={18} className="az-item-icon" aria-hidden="true" />
      <span className="az-item-title">{title}</span>
      <ChevronRight size={16} className={`az-item-chevron ${isOpen ? 'az-item-chevron--open' : ''}`} aria-hidden="true" />
    </button>
    {isOpen && (
      <div className="az-item-content">
        {typeof content === 'string' ? <p>{content}</p> : content}
      </div>
    )}
  </div>
);

const About = () => {
  const { focusFirstHeading, announce } = useAccessibility();
  useFocusableNavigation();
  const [openSection, setOpenSection] = useState(null);

  useEffect(() => {
    // A direct link/bookmark to #contact (e.g. from the /contact redirect
    // below) needs an explicit scroll — React Router's client-side
    // navigation doesn't trigger the browser's native hash-scroll-on-load
    // behavior the way a full page load does.
    if (window.location.hash === '#contact') {
      const target = document.getElementById('contact');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        target.focus?.();
        return;
      }
    }
    focusFirstHeading();
    announce('About Communication Matters page loaded');
  }, [focusFirstHeading, announce]);

  const teamBySection = (section) => TEAM_MEMBERS.filter((m) => m.section === section);

  return (
    <div className="about-page" data-testid="aboutPage">
      <div className="about-container">
        <header className="about-header">
          <h1>Communication Matters</h1>
          <p className="about-subtitle">Empowering Communication for All</p>
        </header>

        <section className="about-section">
          <h2>About the Conference</h2>
          <p>
            Communication Matters is the UK&rsquo;s leading conference on Augmentative and Alternative Communication (AAC).
            We bring together professionals, families, and individuals who use AAC to share knowledge, experiences, and
            innovations in the field of communication support.
          </p>
          <p>
            Our mission is to promote and support the use of AAC to enable people with communication difficulties to
            participate fully in society.
          </p>
        </section>

        <section className="about-section">
          <h2>Conference A&ndash;Z</h2>
          <p className="about-section-intro">Everything you need to know about the event.</p>
          <div className="az-list">
            {CONFERENCE_AZ.map((item) => (
              <InfoDisclosure
                key={item.title}
                title={item.title}
                icon={item.icon}
                content={item.content}
                isOpen={openSection === item.title}
                onToggle={() => setOpenSection((prev) => (prev === item.title ? null : item.title))}
              />
            ))}
          </div>
        </section>

        <section className="about-section" id="contact">
          <h2>Contact Information</h2>
          <div className="contact-rows">
            <div className="contact-row">
              <Globe size={18} aria-hidden="true" />
              <div>
                <span className="contact-row-title">Website</span>
                <span className="contact-row-value">www.communicationmatters.org.uk</span>
              </div>
            </div>
            <div className="contact-row">
              <Mail size={18} aria-hidden="true" />
              <div>
                <span className="contact-row-title">Email</span>
                <span className="contact-row-value">admin@communicationmatters.org.uk</span>
              </div>
            </div>
            <div className="contact-row">
              <Phone size={18} aria-hidden="true" />
              <div>
                <span className="contact-row-title">Phone</span>
                <span className="contact-row-value">+44 (0)113 343 0151</span>
              </div>
            </div>
          </div>
        </section>

        <section className="about-section">
          <h2>Emergency Contacts</h2>
          <ul className="emergency-list">
            {EMERGENCY_CONTACTS.map((contact) => (
              <li key={contact.name} className={`emergency-row ${contact.isPrimary ? 'emergency-row--primary' : ''}`}>
                <a href={`tel:${contact.number.replace(/\s/g, '')}`}>
                  <span className="emergency-name">{contact.name}</span>
                  <span className="emergency-number">{contact.number}</span>
                </a>
              </li>
            ))}
          </ul>
        </section>

        <section className="about-section">
          <h2>Meet the Team</h2>
          {['Staff', 'Trustees', 'Patrons'].map((section) => {
            const members = teamBySection(section);
            if (members.length === 0) return null;
            return (
              <div key={section} className="team-section-group">
                <h3 className="team-section-label">{section.toUpperCase()}</h3>
                <div className="team-grid">
                  {members.map((member) => (
                    <div key={member.name} className="team-member">
                      <div className="team-member-avatar" aria-hidden="true">{initials(member.name)}</div>
                      <h4 className="member-name">{member.name}</h4>
                      <p className="member-role">{member.role}</p>
                      <p className="member-bio">{member.bio}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </section>
      </div>
    </div>
  );
};

export default About;
